import LocomotiveScroll from 'locomotive-scroll';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const motion = {
    enterDuration: 0.72,
    interactionDuration: 0.32,
    easeOut: 'power3.out',
    easeInOut: 'power3.inOut',
    stagger: 0.08,
};

const stackAnimations = [
    {
        trigger: '.makerspace-trigger',
        tiles: '.makerspace-tile1, .makerspace-tile2, .makerspace-tile3, .makerspace-tile4',
        offsets: [
            { xPercent: -12 },
            { yPercent: -12 },
            { xPercent: 12 },
            { yPercent: 12 },
        ],
    },
    {
        trigger: '.savvy-trigger',
        tiles: '.savvy-tile1, .savvy-tile2, .savvy-tile3',
        offsets: [
            { yPercent: -12 },
            { xPercent: 12 },
            { xPercent: -12 },
        ],
    },
    {
        trigger: '.educast-trigger',
        tiles: '.educast-tile1, .educast-tile2, .educast-tile3',
        offsets: [
            { yPercent: -12 },
            { xPercent: 12 },
            { xPercent: -12 },
        ],
    },
    {
        trigger: '.evesms-trigger',
        tiles: '.evesms-tile1, .evesms-tile2, .evesms-tile3',
        offsets: [
            { yPercent: -12 },
            { xPercent: 12 },
            { xPercent: -12 },
        ],
    },
    {
        trigger: '.sims-trigger',
        tiles: '.sims-tile1, .sims-tile2, .sims-tile3',
        offsets: [
            { yPercent: -12 },
            { xPercent: 12 },
            { xPercent: -12 },
        ],
    },
];

const productSections = [
    '#computhinkers',
    '#learnit',
    '#roboclass',
    '#makerspace',
    '#savvy',
    '#eve',
    '#educast',
    '#sims',
];

function initPageTransition(reduceMotion) {
    const preloader = document.querySelector('.preloader');

    if (!preloader) {
        document.body.classList.add('loaded');
        return;
    }

    if (reduceMotion) {
        gsap.set(preloader, { autoAlpha: 0 });
        document.body.classList.add('loaded');
        return;
    }

    const introElements = document.querySelectorAll([
        'main > section:first-child header > *',
        'main > section:first-child .ed',
        'main .content img',
        'main .content .circular-button',
    ].join(', '));

    const timeline = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: () => document.body.classList.add('loaded'),
    });

    timeline
        .to(preloader.querySelector('.loader'), {
            autoAlpha: 0,
            scale: 0.85,
            duration: 0.2,
        })
        .to(preloader, {
            autoAlpha: 0,
            duration: 0.45,
        }, '<0.05');

    if (introElements.length) {
        timeline.from(introElements, {
            autoAlpha: 0,
            y: 32,
            duration: 0.75,
            stagger: 0.09,
            clearProps: 'transform,opacity,visibility',
        }, '-=0.2');
    }
}

function initSmoothScroll() {
    const locomotiveScroll = new LocomotiveScroll({
        scrollCallback: ScrollTrigger.update,
        lenisOptions: {
            duration: 1.05,
            smoothWheel: true,
            wheelMultiplier: 0.9,
        },
    });

    const refreshMeasurements = () => locomotiveScroll.resize();
    const refreshAfterLoad = () => {
        locomotiveScroll.resize();
        ScrollTrigger.refresh();
    };

    ScrollTrigger.addEventListener('refresh', refreshMeasurements);
    window.addEventListener('load', refreshAfterLoad, { once: true });

    return () => {
        ScrollTrigger.removeEventListener('refresh', refreshMeasurements);
        window.removeEventListener('load', refreshAfterLoad);
        locomotiveScroll.destroy();
    };
}

function initProductsScrollAnimation(isDesktop) {
    const productsRow = document.querySelector('.products-row');
    const firstRow = document.querySelector('.products-row1');
    const secondRow = document.querySelector('.products-row2');

    if (!productsRow || !firstRow || !secondRow) {
        return () => {};
    }

    const distance = isDesktop ? 200 : 64;
    const timeline = gsap.timeline({
        scrollTrigger: {
            trigger: productsRow,
            start: 'bottom 80%',
            end: 'bottom 20%',
            scrub: 0.5,
        },
    });

    timeline
        .fromTo(firstRow, { x: 0, autoAlpha: 1 }, {
            x: distance,
            autoAlpha: 0,
            duration: 1.2,
            ease: motion.easeInOut,
        })
        .fromTo(secondRow, { x: 0, autoAlpha: 1 }, {
            x: -distance,
            autoAlpha: 0,
            duration: 1.2,
            ease: motion.easeInOut,
        }, 0);

    return () => {
        timeline.scrollTrigger?.kill();
        timeline.kill();
        gsap.set([firstRow, secondRow], { clearProps: 'transform,opacity,visibility' });
    };
}

function initStackScrollAnimations(isDesktop) {
    const timelines = [];

    stackAnimations.forEach(({ trigger, tiles: selector, offsets }) => {
        const triggerElement = document.querySelector(trigger);
        const tiles = gsap.utils.toArray(selector);

        if (!triggerElement || !tiles.length) {
            return;
        }

        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: triggerElement,
                start: 'clamp(top 88%)',
                end: 'clamp(bottom 38%)',
                scrub: 0.75,
                onToggle: ({ isActive }) => {
                    gsap.set(tiles, {
                        willChange: isActive ? 'transform,opacity' : 'auto',
                    });
                },
            },
        }).fromTo(tiles, {
            autoAlpha: isDesktop ? 0.18 : 0.5,
            scale: isDesktop ? 0.92 : 0.97,
            rotation: (index) => isDesktop ? (index % 2 === 0 ? -2.5 : 2.5) : 0,
            xPercent: (index) => (offsets[index]?.xPercent ?? 0) * (isDesktop ? 1 : 0.45),
            yPercent: (index) => (offsets[index]?.yPercent ?? 0) * (isDesktop ? 1 : 0.45),
        }, {
            autoAlpha: 1,
            scale: 1,
            xPercent: 0,
            yPercent: 0,
            rotation: 0,
            ease: 'none',
            stagger: 0.08,
        });

        timelines.push(timeline);
    });

    return () => {
        timelines.forEach((timeline) => {
            timeline.scrollTrigger?.kill();
            timeline.kill();
        });
    };
}

function initSectionReveals() {
    const animations = [];

    productSections.forEach((selector) => {
        const section = document.querySelector(selector);

        if (!section) {
            return;
        }

        const logo = section.querySelector('.logo');
        const description = section.querySelector('.description');
        const targets = [logo, description].filter(Boolean);

        if (!targets.length) {
            return;
        }

        let timeline;
        const scrollTrigger = ScrollTrigger.create({
            trigger: section,
            start: 'top 72%',
            once: true,
            onEnter: () => {
                timeline = gsap.timeline({
                    defaults: {
                        duration: motion.enterDuration,
                        ease: motion.easeOut,
                    },
                }).fromTo(targets, {
                    autoAlpha: 0,
                    y: 28,
                }, {
                    autoAlpha: 1,
                    y: 0,
                    stagger: 0.12,
                    clearProps: 'transform,opacity,visibility',
                });
            },
        });

        animations.push({ scrollTrigger, getTimeline: () => timeline });
    });

    return () => {
        animations.forEach(({ scrollTrigger, getTimeline }) => {
            scrollTrigger.kill();
            getTimeline()?.kill();
        });
    };
}

function initSupportingReveals(isDesktop) {
    const revealTargets = gsap.utils.toArray([
        '#schedule .schedule',
        '#highlight header',
        '#highlight .glide',
        'html.highlights-page #multimedia figure',
        'html.about-page #mission-vision .vision',
        'html.about-page #mission-vision .mission',
        '#footer .left-pane',
        '#footer .right-pane',
        '#footer-simple .socials',
        '#footer-simple .container-fluid > .row > .col-12:last-child',
    ].join(', '));

    if (!revealTargets.length) {
        return () => {};
    }

    const triggers = ScrollTrigger.batch(revealTargets, {
        start: 'top 88%',
        once: true,
        interval: 0.1,
        batchMax: 4,
        onEnter: (elements) => {
            gsap.fromTo(elements, {
                autoAlpha: 0,
                y: isDesktop ? 30 : 18,
                scale: isDesktop ? 0.985 : 1,
            }, {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: motion.enterDuration,
                ease: motion.easeOut,
                stagger: 0.1,
                overwrite: 'auto',
                clearProps: 'transform,opacity,visibility',
            });
        },
    });

    return () => triggers.forEach((trigger) => trigger.kill());
}

function initCompetitionCarouselMotion() {
    const carousel = document.querySelector('#highlight .highlight');

    if (!carousel) {
        return () => {};
    }

    const updateSlides = () => {
        carousel.querySelectorAll('.glide__slide img').forEach((image) => {
            const isActive = image.closest('.glide__slide')?.classList.contains('glide__slide--active');

            gsap.to(image, {
                autoAlpha: isActive ? 1 : 0.68,
                scale: isActive ? 1 : 0.94,
                duration: 0.5,
                ease: motion.easeOut,
                overwrite: 'auto',
            });
        });
    };

    const observer = new MutationObserver(updateSlides);

    observer.observe(carousel, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
    });

    updateSlides();

    return () => {
        observer.disconnect();
        gsap.set(carousel.querySelectorAll('.glide__slide img'), {
            clearProps: 'transform,opacity,visibility',
        });
    };
}

function initCirclePointer() {
    const circlePointer = document.querySelector('.circle-pointer');

    if (!circlePointer) {
        return () => {};
    }

    gsap.set(circlePointer, {
        xPercent: -50,
        yPercent: -50,
        autoAlpha: 0,
        willChange: 'transform',
    });

    const moveX = gsap.quickTo(circlePointer, 'x', {
        duration: 0.28,
        ease: 'power3.out',
    });
    const moveY = gsap.quickTo(circlePointer, 'y', {
        duration: 0.28,
        ease: 'power3.out',
    });

    let pointerVisible = false;

    const handlePointerMove = (event) => {
        moveX(event.clientX);
        moveY(event.clientY);

        if (!pointerVisible) {
            pointerVisible = true;
            gsap.to(circlePointer, {
                autoAlpha: 1,
                duration: 0.2,
                overwrite: 'auto',
            });
        }
    };

    const handlePointerOver = (event) => {
        if (!event.target.closest('a, button, .menu-icon')) {
            return;
        }

        gsap.to(circlePointer, {
            scale: 2,
            backgroundColor: 'rgba(var(--yellow-rgb), 0.8)',
            duration: 0.2,
            overwrite: 'auto',
        });
    };

    const handlePointerOut = (event) => {
        if (!event.target.closest('a, button, .menu-icon')) {
            return;
        }

        gsap.to(circlePointer, {
            scale: 1,
            backgroundColor: 'rgba(var(--green-rgb), 0.6)',
            duration: 0.2,
            overwrite: 'auto',
        });
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.addEventListener('pointerover', handlePointerOver);
    document.addEventListener('pointerout', handlePointerOut);

    return () => {
        window.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerover', handlePointerOver);
        document.removeEventListener('pointerout', handlePointerOut);
    };
}

function initCircularButtonPulse() {
    const button = document.querySelector('.circular-button');
    const pulse = button?.querySelector('.circular-button__pulse');

    if (!button || !pulse) {
        return () => {};
    }

    const timeline = gsap.timeline({
        delay: 1.1,
        repeat: -1,
        repeatDelay: 0.45,
    });

    timeline
        .addLabel('beat')
        .to(button, {
            scale: 1.035,
            duration: 0.16,
            ease: 'power2.out',
        }, 'beat')
        .to(button, {
            scale: 1,
            duration: 0.38,
            ease: 'back.out(2)',
        })
        .fromTo(pulse, {
            autoAlpha: 0.58,
            scale: 1,
        }, {
            autoAlpha: 0,
            scale: 1.52,
            duration: 1.05,
            ease: 'power2.out',
        }, 'beat');

    return () => {
        timeline.kill();
        gsap.set([button, pulse], {
            clearProps: 'transform,opacity,visibility',
        });
    };
}

function initCircularButtonDistortion() {
    const button = document.querySelector('.circular-button');
    const surface = button?.querySelector('.circular-button__surface');

    if (!button || !surface) {
        return () => {};
    }

    const scaleXTo = gsap.quickTo(surface, 'scaleX', {
        duration: 0.3,
        ease: 'power3.out',
    });
    const scaleYTo = gsap.quickTo(surface, 'scaleY', {
        duration: 0.3,
        ease: 'power3.out',
    });
    const xTo = gsap.quickTo(surface, 'x', {
        duration: 0.3,
        ease: 'power3.out',
    });
    const yTo = gsap.quickTo(surface, 'y', {
        duration: 0.3,
        ease: 'power3.out',
    });

    const handlePointerMove = (event) => {
        if (event.pointerType && !['mouse', 'pen'].includes(event.pointerType)) {
            return;
        }

        const bounds = button.getBoundingClientRect();
        const normalizedX = gsap.utils.clamp(-1, 1, (event.clientX - bounds.left - bounds.width / 2) / (bounds.width / 2));
        const normalizedY = gsap.utils.clamp(-1, 1, (event.clientY - bounds.top - bounds.height / 2) / (bounds.height / 2));
        const horizontalPull = Math.abs(normalizedX);
        const verticalPull = Math.abs(normalizedY);

        scaleXTo(1 + horizontalPull * 0.13 - verticalPull * 0.045);
        scaleYTo(1 + verticalPull * 0.13 - horizontalPull * 0.045);
        xTo(normalizedX * 5);
        yTo(normalizedY * 5);
    };

    const handlePointerLeave = () => {
        scaleXTo(1);
        scaleYTo(1);
        xTo(0);
        yTo(0);
    };

    button.addEventListener('pointermove', handlePointerMove, { passive: true });
    button.addEventListener('pointerleave', handlePointerLeave);

    return () => {
        button.removeEventListener('pointermove', handlePointerMove);
        button.removeEventListener('pointerleave', handlePointerLeave);
        gsap.set(surface, { clearProps: 'transform' });
    };
}

function initializeMotion() {
    const media = gsap.matchMedia();
    const isLandingPage = document.documentElement.classList.contains('landing-page');

    media.add({
        all: 'all',
        isDesktop: '(min-width: 768px)',
        reduceMotion: '(prefers-reduced-motion: reduce)',
        finePointer: '(hover: hover) and (pointer: fine)',
    }, (context) => {
        const { isDesktop, reduceMotion, finePointer } = context.conditions;
        const cleanups = [];

        initPageTransition(reduceMotion);

        if (reduceMotion) {
            gsap.set([
                '.products-row1',
                '.products-row2',
                '.stack-image',
                ...productSections.map((selector) => `${selector} .logo, ${selector} .description`),
            ], {
                clearProps: 'all',
            });
        } else {
            if (!isLandingPage) {
                cleanups.push(initSmoothScroll());
            }

            cleanups.push(initProductsScrollAnimation(isDesktop));
            cleanups.push(initStackScrollAnimations(isDesktop));
            cleanups.push(initSectionReveals());
            cleanups.push(initSupportingReveals(isDesktop));
            cleanups.push(initCompetitionCarouselMotion());
            cleanups.push(initCircularButtonPulse());
            cleanups.push(initCircularButtonDistortion());
        }

        if (finePointer && !reduceMotion) {
            cleanups.push(initCirclePointer());
        } else {
            gsap.set('.circle-pointer', { display: 'none' });
        }

        return () => cleanups.forEach((cleanup) => cleanup());
    });
}

document.addEventListener('DOMContentLoaded', initializeMotion);
